import type { ComponentType } from 'react';
import { AgreesIcon, BulletListIcon, ClosedLockIcon, DiffIcon, PricelistIcon, StatsIcon, ViewIcon } from '@entur/icons';
import { type AccessContext, type AccessRule, canAccess, routeAccess } from 'app/access';
import * as permissions from 'permissions';

const { getPermission, READ, CREATE, EDIT, REPORTS, SETTLEMENTS } = permissions;

export interface MenuItem {
  /** Matches the path segment NavBar uses to mark the item active. */
  key: string;
  labelId: string;
  path: (chartOfAccountsId?: number) => string;
  /** The rule of the route the item links to. */
  access: AccessRule;
  /** Extra requirement for showing the item in the menu, on top of route access. */
  menuOnly?: AccessRule;
}

export interface MenuGroup {
  key: string;
  labelId: string;
  Icon: ComponentType<{ className?: string }>;
  items: MenuItem[];
}

const chartPath = (segment: string) => (chartOfAccountsId?: number) =>
  `/chart-of-accounts/${chartOfAccountsId}/${segment}`;

export const menuGroups: MenuGroup[] = [
  {
    key: 'clearing',
    labelId: 'menu.clearing',
    Icon: AgreesIcon,
    items: [
      { key: 'agreements', labelId: 'menu.agreements', path: chartPath('agreements'), access: routeAccess.agreements },
      {
        key: 'clearing-mappings',
        labelId: 'menu.clearingMappings',
        path: chartPath('clearing-mappings'),
        access: routeAccess.clearingMappings,
      },
      {
        key: 'accounting-mappings',
        labelId: 'menu.accounting-mappings',
        path: chartPath('accounting-mappings'),
        access: routeAccess.accountingMappings,
      },
      {
        key: 'accounting-rules',
        labelId: 'menu.accountingRules',
        path: chartPath('accounting-rules'),
        access: routeAccess.accountingRules,
      },
      {
        key: 'settled-data',
        labelId: 'menu.settled-data',
        path: () => '/settled-data',
        access: routeAccess.settledData,
      },
    ],
  },
  {
    key: 'settlementsHeader',
    labelId: 'menu.settlements',
    Icon: DiffIcon,
    items: [
      {
        key: 'settlements',
        labelId: 'menu.settlementSearch',
        path: () => '/settlements',
        access: routeAccess.settlements,
      },
      {
        key: 'incoming-settlements',
        labelId: 'menu.incoming-settlements',
        path: () => '/incoming-settlements',
        access: routeAccess.settlements,
      },
      {
        key: 'settlement-sign-off',
        labelId: 'menu.settlement-sign-off',
        path: () => '/settlement-sign-off',
        access: routeAccess.settlements,
      },
      {
        key: 'incoming-annexes',
        labelId: 'menu.incoming-annexes',
        path: () => '/incoming-annexes',
        access: routeAccess.settlements,
      },
      {
        key: 'annex-clearing-result',
        labelId: 'menu.annex-clearing-results',
        path: () => '/annex-clearing-result',
        access: routeAccess.settlements,
      },
      {
        key: 'manual-settlement',
        labelId: 'menu.manual-settlement',
        path: () => '/manual-settlement',
        access: routeAccess.settlementsCreate,
        menuOnly: { permissions: getPermission(SETTLEMENTS, READ) },
      },
    ],
  },
  {
    key: 'reports',
    labelId: 'menu.reports',
    Icon: PricelistIcon,
    items: [
      {
        key: 'report-search',
        labelId: 'menu.reportSearch',
        path: () => '/report-search',
        access: routeAccess.reportSearch,
      },
      {
        key: 'report-templates',
        labelId: 'menu.reportTemplates',
        path: () => '/report-templates',
        access: routeAccess.reportTemplates,
        menuOnly: { permissions: getPermission(REPORTS, EDIT) },
      },
      {
        key: 'report-production',
        labelId: 'menu.reportProduction',
        path: () => '/report-production',
        access: routeAccess.reportProduction,
        menuOnly: { permissions: getPermission(REPORTS, READ) },
      },
      {
        key: 'report-statistics',
        labelId: 'menu.reportStatistics',
        path: () => '/report-statistics',
        access: routeAccess.reportStatistics,
        menuOnly: { permissions: getPermission(REPORTS, CREATE) },
      },
      {
        key: 'failed-reports',
        labelId: 'menu.failedReports',
        path: () => '/failed-reports',
        access: routeAccess.failedReports,
      },
    ],
  },
  {
    key: 'register',
    labelId: 'menu.register',
    Icon: BulletListIcon,
    items: [
      {
        key: 'point-of-sale',
        labelId: 'menu.pos-registry',
        path: () => '/point-of-sale',
        access: routeAccess.register,
      },
      {
        key: 'dist-channels',
        labelId: 'menu.dist-channels',
        path: () => '/dist-channels',
        access: routeAccess.register,
      },
      { key: 'lines', labelId: 'menu.lines', path: chartPath('lines'), access: routeAccess.lines },
    ],
  },
  {
    key: 'accountPlan',
    labelId: 'menu.chartOfAccounts',
    Icon: StatsIcon,
    items: [2, 3, 4, 5, 6, 7].map((n) => ({
      key: `segment${n}`,
      labelId: `menu.segment${n}`,
      path: chartPath(`segment${n}`),
      access: routeAccess.accountCodePlan,
    })),
  },
  {
    key: 'admin',
    labelId: 'menu.admin',
    Icon: ClosedLockIcon,
    items: [
      {
        key: 'accounting-months',
        labelId: 'menu.accountingMonth',
        path: () => '/accounting-months',
        access: routeAccess.admin,
      },
      {
        key: 'operating-messages',
        labelId: 'menu.operatingMessages',
        path: () => '/operating-messages',
        access: routeAccess.admin,
      },
      { key: 'audit-log', labelId: 'menu.audit-log', path: () => '/audit-log', access: routeAccess.admin },
      {
        key: 'manual-refs',
        labelId: 'menu.manual-refs',
        path: () => '/manual-refs',
        access: routeAccess.admin,
      },
      { key: 'partners', labelId: 'menu.partners', path: () => '/partners', access: routeAccess.admin },
      {
        key: 'charts-of-accounts',
        labelId: 'chartsOfAccounts.namePlural',
        path: () => '/charts-of-accounts',
        access: routeAccess.admin,
      },
      {
        key: 'transaction-types',
        labelId: 'transactionTypes.namePlural',
        path: () => '/transaction-types',
        access: routeAccess.admin,
      },
      {
        key: 'data-products',
        labelId: 'menu.dataProducts',
        path: () => '/data-products',
        access: routeAccess.admin,
      },
    ],
  },
  {
    key: 'surveillance',
    labelId: 'menu.surveillance',
    Icon: ViewIcon,
    items: [{ key: 'xledger', labelId: 'menu.xledger', path: () => '/xledger', access: routeAccess.monitoring }],
  },
];

/** Groups with only the items the user may open; groups left empty are dropped. */
export const getVisibleMenuGroups = (context: AccessContext) =>
  menuGroups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => canAccess(item.access, context) && (!item.menuOnly || canAccess(item.menuOnly, context))
      ),
    }))
    .filter((group) => group.items.length > 0);

export const getMenuGroupKey = (itemKey?: string) =>
  menuGroups.find((group) => group.items.some((item) => item.key === itemKey))?.key ?? null;
